// IssuesScreen - §11 지연 및 이슈관리.
//
// 데이터 소스는 `issues` 컬렉션이 아니다. 서버가 상태 `red` 인 마일스톤 ·
// 준비항목 · 생산계획을 계산으로 산출한 뒤 `issues` 를 left-join 한다
// (REQ-ISSUE-003). **사유가 없는 지연도 반드시 목록에 나온다** - 사라지면
// §20-② "지연되고 있는 개발품은 무엇인가" 가 깨진다.
//
// 모든 행이 지연이라 행 틴트만으로는 위계가 안 생긴다. 그래서 위계를
// **지연일수** 가 만든다: 7일 이상은 17px 700 + 삼각형, 3일 이상은 13px 600,
// 1~2일은 주의색 (디자인 가이드 §5).
import { IssueForm, type IssueTarget } from "@/components/IssueForm";
import { StatusDot } from "@/components/StatusIndicator";
import { THead, Table, TableScroll, Td, Th, Tr, TrGroup } from "@/components/Table";
import {
  Button,
  DataBoundary,
  EmptyState,
  FilterBar,
  Select,
  TableSkeleton,
  Tooltip,
} from "@/components/ui";
import type { Customer, IssueRow, IssueTargetType, Staff } from "@/lib/api";
import { asOfLabel, monthDay } from "@/lib/dates";
import { EMPTY } from "@/lib/format";
import { useIssues } from "@/lib/hooks";
import { todayKst } from "@/lib/status";
import type { ScreenNav } from "@/screens/nav";
import { useState } from "react";

const COL_COUNT = 9;

const TARGET_TYPES: readonly { value: IssueTargetType; label: string }[] = [
  { value: "milestone", label: "마일스톤" },
  { value: "prep", label: "준비항목" },
  { value: "production", label: "생산계획" },
];

export const IssuesScreen = ({
  nav,
  customers,
  staff,
}: {
  nav: ScreenNav;
  customers: readonly Customer[];
  staff: readonly Staff[];
}) => {
  const [today] = useState(() => todayKst());
  const [customerId, setCustomerId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [targetType, setTargetType] = useState<IssueTargetType | "">("");
  const [target, setTarget] = useState<IssueTarget | null>(null);

  const res = useIssues({ customerId, staffId, targetType });
  const rows = res.data;
  const missing = rows.filter((r) => !r.hasIssueDoc).length;
  const filtered = !!customerId || !!staffId || !!targetType;

  const reset = () => {
    setCustomerId("");
    setStaffId("");
    setTargetType("");
  };

  const openIssue = (r: IssueRow) =>
    setTarget({
      devItemId: r.devItemId,
      targetType: r.targetType,
      targetId: r.targetId,
      targetLabel: r.targetLabel,
      issueId: r.issueId,
      reason: r.reason,
      action: r.action,
      expectedDate: r.expectedDate,
    });

  // 서버가 `사유 미입력 우선 -> 지연일수 내림차순 -> 계획일` 로 정렬해 준다
  // (REQ-ISSUE-005). 그 순서를 그대로 쓰고 그룹 헤더만 얹는다.
  let seenWithDoc = false;

  return (
    <div className="flex flex-col gap-s4">
      <header className="flex flex-col gap-s1">
        <p className="text-label" style={{ color: "var(--text-secondary)" }}>
          계획일이 지난 항목만 표시됩니다.
        </p>
        <p
          className="text-caption tabular-nums"
          data-num=""
          style={{ color: "var(--text-tertiary)" }}
        >
          {asOfLabel(today)}
          {rows.length > 0 && ` · 지연 ${rows.length}건 (사유 미입력 ${missing}건)`}
        </p>
      </header>

      <div className="overflow-hidden rounded-card" style={{ background: "var(--surface)" }}>
        <FilterBar
          right={
            <Button variant="ghost" size="sm" onClick={reset} disabled={!filtered}>
              초기화
            </Button>
          }
        >
          <span className="w-[9rem]">
            <Select
              value={customerId}
              onChange={setCustomerId}
              options={customers
                .filter((c) => c.active)
                .map((c) => ({ value: c.id, label: c.name }))}
              ariaLabel="고객사로 조회"
              placeholder="고객사 전체"
            />
          </span>
          <span className="w-[9rem]">
            <Select
              value={staffId}
              onChange={setStaffId}
              options={staff.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name }))}
              ariaLabel="담당자로 조회"
              placeholder="담당자 전체"
            />
          </span>
          <span className="w-[10rem]">
            <Select
              value={targetType}
              onChange={(v) => setTargetType(v as IssueTargetType | "")}
              options={TARGET_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              ariaLabel="지연항목 종류로 조회"
              placeholder="지연항목 전체"
            />
          </span>
        </FilterBar>

        <DataBoundary
          loading={res.loading}
          error={res.error}
          onRetry={() => void res.reload()}
          skeleton={<TableSkeleton rows={5} cols={COL_COUNT} />}
        >
          {rows.length === 0 ? (
            filtered ? (
              <EmptyState
                icon="search"
                title="조건에 맞는 지연 항목이 없어요"
                description="조건을 바꾸면 다른 지연 항목을 볼 수 있어요."
                action={
                  <Button variant="secondary" size="md" onClick={reset}>
                    필터 초기화
                  </Button>
                }
              />
            ) : (
              // 정상은 조용한 사실이다. 축하 일러스트도, 초록 배너도 없다.
              <EmptyState
                icon="check"
                title="지연된 항목이 없습니다"
                description="지연 없이 순항 중이에요. 오늘은 확인하실 게 없어요."
                action={
                  <Button variant="secondary" size="md" onClick={() => nav.goto("overview")}>
                    종합현황으로
                  </Button>
                }
              />
            )
          ) : (
            <TableScroll>
              <Table label="지연 및 이슈">
                <THead>
                  <tr>
                    <Th width={90}>고객사</Th>
                    <Th>품명</Th>
                    <Th width={120}>지연항목</Th>
                    <Th width={76} num>
                      계획일
                    </Th>
                    <Th width={76} num>
                      예상일
                    </Th>
                    <Th width={90} num>
                      지연일수
                    </Th>
                    <Th>사유</Th>
                    <Th width={80}>담당자</Th>
                    <Th width={110}>조치</Th>
                  </tr>
                </THead>
                <tbody>
                  {rows.map((r) => {
                    // 사유 미입력 그룹이 위에 오고, 첫 "사유 있음" 행에서
                    // 구분선이 한 번 들어간다 (결정 S-15).
                    const headMissing = !r.hasIssueDoc && !seenWithDoc && r === rows[0];
                    const headWithDoc = r.hasIssueDoc && !seenWithDoc;
                    if (r.hasIssueDoc) seenWithDoc = true;
                    return (
                      <IssueRowView
                        key={`${r.targetType}:${r.targetId}`}
                        row={r}
                        groupLabel={
                          headMissing ? "사유 미입력" : headWithDoc ? "사유 입력됨" : null
                        }
                        groupCount={
                          headMissing ? missing : headWithDoc ? rows.length - missing : undefined
                        }
                        onOpenItem={nav.openItem}
                        onEdit={() => openIssue(r)}
                      />
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>
          )}
        </DataBoundary>
      </div>

      <IssueForm
        target={target}
        onClose={() => setTarget(null)}
        onSaved={(m) => {
          nav.toast(m);
          void res.reload();
        }}
        onError={(m) => nav.toast(m, "error")}
      />
    </div>
  );
};

const IssueRowView = ({
  row,
  groupLabel,
  groupCount,
  onOpenItem,
  onEdit,
}: {
  row: IssueRow;
  groupLabel: string | null;
  groupCount?: number;
  onOpenItem: (id: string) => void;
  onEdit: () => void;
}) => (
  <>
    {groupLabel && (
      <TrGroup
        label={groupLabel}
        count={groupCount}
        colSpan={COL_COUNT}
        tone={row.hasIssueDoc ? "default" : "late"}
      />
    )}
    <Tr tone="late">
      <Td>{row.customerName || EMPTY}</Td>
      <Td>
        <Button variant="link" size="sm" onClick={() => onOpenItem(row.devItemId)}>
          {row.partName}
        </Button>
      </Td>
      <Td>{row.targetLabel || EMPTY}</Td>
      <Td num>{monthDay(row.plannedDate) || EMPTY}</Td>
      <Td num>{monthDay(row.expectedDate) || EMPTY}</Td>
      <Td num>
        <DelayDays days={row.delayDays} />
      </Td>
      <Td>
        {row.reason ? (
          <Tooltip content={row.action ? `${row.reason} / 조치 ${row.action}` : row.reason}>
            <span className="block max-w-[16rem] truncate">{row.reason}</span>
          </Tooltip>
        ) : (
          <Button variant="link" size="sm" onClick={onEdit}>
            사유 입력
          </Button>
        )}
      </Td>
      <Td>{row.staffName || EMPTY}</Td>
      <Td>
        <Button variant="secondary" size="sm" onClick={onEdit}>
          {row.hasIssueDoc ? "업데이트" : "지연사유 등록하기"}
        </Button>
      </Td>
    </Tr>
  </>
);

/**
 * 이 화면의 유일한 강조 열이다. 7일 이상에만 삼각형 글리프가 붙는다 -
 * 32행이 전부 삼각형이면 강조가 아니라 배경이 된다.
 */
const DelayDays = ({ days }: { days: number }) => {
  const severe = days >= 7;
  const notable = days >= 3;
  return (
    <span
      className={[
        "inline-flex items-center justify-end gap-s1 tabular-nums",
        severe
          ? "text-heading font-bold"
          : notable
            ? "text-label font-semibold"
            : "text-label font-medium",
      ].join(" ")}
      style={{ color: notable ? "var(--state-late)" : "var(--state-warn)" }}
    >
      {severe && <StatusDot status="late" size="row" labelled />}
      {`${days}일`}
    </span>
  );
};
